import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { useCustomHotkeys } from '../../../../common/hooks/useCustomHotkeys';
import { EditorMode } from '../../../../modules/editor/models/EditorModes';
import { AnnotationConfirmationModal, AnnotationConfirmationModalMode } from '../../../components/AnnotationConfirmationModal';
import { SaveBeforeRejectConfirmationModal } from '../../../components/SaveBeforeRejectConfirmationModal';
import { useLoader } from '../../../helpers/useLoader';
import { useAnnotationPermissions } from '../../annotation/annotation.permissions';
import { IAnnotationService } from '../../annotation/annotation.service';
import { CorrectNavigation } from '../../annotation/components/CorrectNavigation';
import { IFreeDrawSegmentationService } from '../../annotation/freeDrawSegmentation.service';
import { IAnnotationTypeBl } from '../../annotation/submodules/annotationTypes/annotationType.bl';
import { IReviewRejectReasonsStore } from '../../annotation/submodules/reviewRejectReasones/reviewRejectReasons.store';
import { IUndoRedoHistory } from '../../annotation/undoRedoHistory.service';
import { IFreeAccessService } from '../freeAccess.service';
import { IFreeAccessStore } from '../freeAccess.store';

export const FreeAccessReviewControlsCorrectContainer = observer((): ReactElement => {
  const freeAccessService = useInjection(IFreeAccessService.$);
  const freeAccessStore = useInjection(IFreeAccessStore.$);
  const freeDrawSegmentationService = useInjection(IFreeDrawSegmentationService.$);
  const historyService = useInjection(IUndoRedoHistory.$);
  const reviewRejectReasonsStore = useInjection(IReviewRejectReasonsStore.$);
  const annotationService = useInjection(IAnnotationService.$);
  const annotationTypeBl = useInjection(IAnnotationTypeBl.$);
  const permissions = useAnnotationPermissions();

  const { isLoading, withLoaderAsync } = useLoader();

  const [isCancelModalShown, setIsCancelModalShown] = useState(false);
  const [showAnnotationConfirmationModal, setShowAnnotationConfirmationModal] = useState(false);
  const [annotationConfirmationModalMode, setAnnotationConfirmationModalMode] = useState<AnnotationConfirmationModalMode>(
    AnnotationConfirmationModalMode.SUBMIT
  );
  const [showDiscardConfirmationModal, setShowDiscardConfirmationModal] = useState(false);
  const [showSaveBeforeRejectConfirmationModal, setShowSaveBeforeRejectConfirmationModal] = useState(false);

  const handleSubmitAndAcceptAsync = async (): Promise<void> => {
    setShowAnnotationConfirmationModal(false);
    await withLoaderAsync(async () => {
      await freeAccessService.editAndAcceptOrRejectAnnotationAsync(true);
    }, 'submit-and-accept-button');
  };

  const handleReject = async (): Promise<void> => {
    if (!annotationService.validateAnnotations()) {
      await annotationTypeBl.handleReject();
      return;
    }

    setShowSaveBeforeRejectConfirmationModal(false);
    toggleAnnotationConfirmationModal(AnnotationConfirmationModalMode.REJECT);
  };

  const handleConfirmReject = async (reason?: string): Promise<void> => {
    setShowAnnotationConfirmationModal(false);
    if (reason) {
      await handleRejectConfirmed(reason);
    } else {
      setShowSaveBeforeRejectConfirmationModal(true);
    }
  };

  const handleRejectConfirmed = async (reason?: string): Promise<void> => {
    setShowSaveBeforeRejectConfirmationModal(false);
    freeAccessStore.rejectionReason = reason;
    await withLoaderAsync(async () => {
      await freeAccessService.rejectAnnotationAsync(reason);
    }, 'reject-button');
  };

  const handleSaveBeforeRejectConfirmed = async (): Promise<void> => {
    await handleRejectConfirmed();
  };

  const handleRejectCanceled = (): void => {
    setShowSaveBeforeRejectConfirmationModal(false);
    setShowAnnotationConfirmationModal(false);
  };

  const handleDiscardConfirmed = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    setShowAnnotationConfirmationModal(false);
    await withLoaderAsync(async () => {
      await freeAccessService.discardAnnotationAsync(EditorMode.FREEACCESS_REVIEWEDIT);
    }, 'discard-annotation-button');
  };

  const handleDiscardCanceled = (): void => {
    setShowDiscardConfirmationModal(false);
  };

  const handleDiscard = (): void => {
    setShowDiscardConfirmationModal(true);
  };

  const handleCancelConfirm = async (): Promise<void> => {
    setIsCancelModalShown(false);
    if (!(await freeDrawSegmentationService.clearAsync())) {
      return;
    }
    historyService.clearHistory();
    await withLoaderAsync(async () => {
      await freeAccessService.cancelCorrectAnnotationAsync();
    }, 'cancel-annotation-button');
  };

  const handleCancelCancel = (): void => {
    setIsCancelModalShown(false);
  };

  const handleCancel = async (): Promise<void> => {
    if (!historyService.canUndo) {
      await handleCancelConfirm();
    } else {
      setIsCancelModalShown(true);
    }
  };

  const toggleAnnotationConfirmationModal = (mode?: AnnotationConfirmationModalMode): void => {
    if (mode !== undefined) {
      setAnnotationConfirmationModalMode(mode);
    }
    setShowAnnotationConfirmationModal((prev) => !prev);
  };

  const disabled = isLoading || freeDrawSegmentationService.freeDrawInProgress || freeDrawSegmentationService.freeDrawFeature !== undefined;

  const handleAcceptHotkey = (): void => {
    setAnnotationConfirmationModalMode(AnnotationConfirmationModalMode.SUBMITANDACCEPT);
    toggleAnnotationConfirmationModal();
  };

  const handleRejectHotkey = (): void => {
    setAnnotationConfirmationModalMode(AnnotationConfirmationModalMode.REJECT);
    toggleAnnotationConfirmationModal();
  };

  useCustomHotkeys('ctrl+shift+enter', handleAcceptHotkey, { enabled: !disabled }, [handleAcceptHotkey]);
  useCustomHotkeys('ctrl+shift+backspace', handleRejectHotkey, { enabled: !disabled }, [handleRejectHotkey]);

  return (
    <>
      <CorrectNavigation
        disabled={disabled}
        canDiscard={permissions.canDiscard().allowed}
        editorMode={EditorMode.FREEACCESS_REVIEWEDIT}
        onSubmitAndAccept={handleSubmitAndAcceptAsync}
        onReject={handleReject}
        onCancel={handleCancel}
        onConfirmCancel={handleCancelConfirm}
        onCancelCancel={handleCancelCancel}
        showCancelModal={isCancelModalShown}
        showDiscardModal={showDiscardConfirmationModal}
        onDiscardCanceled={handleDiscardCanceled}
        onDiscardConfirmed={handleDiscardConfirmed}
        onDiscard={handleDiscard}
      />
      <SaveBeforeRejectConfirmationModal
        onSaveBeforeRejectCancel={handleRejectCanceled}
        onSaveBeforeRejectConfirm={handleSaveBeforeRejectConfirmed}
        onSaveBeforeRejectDecline={handleReject}
        showSaveBeforeRejectModal={showSaveBeforeRejectConfirmationModal}
      />
      <AnnotationConfirmationModal
        showAnnotationConfirmationModal={showAnnotationConfirmationModal}
        annotationConfirmationModalMode={annotationConfirmationModalMode}
        rejectAutoCompleteOptions={reviewRejectReasonsStore.rejectionReasons}
        onAnnotationConfirm={
          annotationConfirmationModalMode === AnnotationConfirmationModalMode.SUBMITANDACCEPT ? handleSubmitAndAcceptAsync : handleConfirmReject
        }
        onAnnotationCancel={toggleAnnotationConfirmationModal}
      />
    </>
  );
});
